import React, { useState, useEffect } from 'react';
import {
  ScaleIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface LawInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: 'case-law-analysis' | 'legal-document-drafting' | 'constitutional-interpretation' | 'evidence-evaluation' | 'ethical-dilemma';
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const LawInteractive: React.FC<LawInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  
  // Tracking states
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [minimumEngagementTime] = useState(300); // 5 minutes for legal analysis
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  useEffect(() => {
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        toast('⚠️ Stay focused on your legal analysis for accurate assessment.', {
          icon: '⚖️',
          duration: 3000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitted, tabSwitches]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      if (windowFocused) {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumEngagementTime && !isEngaged) {
            setIsEngaged(true);
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowFocused, isSubmitted, minimumEngagementTime, isEngaged]);

  const initializeExercise = () => {
    switch (exerciseType) {
      case 'case-law-analysis':
        setInputs({
          facts: '',
          legalIssues: ['', ''],
          relevantLaws: ['', ''],
          analysis: '',
          conclusion: '',
          precedents: ['']
        });
        break;
      case 'legal-document-drafting':
        setInputs({
          documentType: '',
          parties: ['', ''],
          terms: ['', '', ''],
          legalLanguage: '',
          clauses: ['', ''],
          signatures: ''
        });
        break;
      case 'constitutional-interpretation':
        setInputs({
          constitutionalProvision: '',
          interpretation: '',
          precedents: ['', ''],
          reasoning: '',
          implications: '',
          dissenting: ''
        });
        break;
      case 'evidence-evaluation':
        setInputs({
          evidenceTypes: [],
          admissibility: '',
          relevance: '',
          reliability: '',
          objections: [''],
          ruling: ''
        });
        break;
      case 'ethical-dilemma':
        setInputs({
          ethicalIssues: [''],
          conflictingDuties: ['', ''],
          ethicalRules: [''],
          resolution: '',
          justification: ''
        });
        break;
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setInputInteractions(prev => prev + 1);
  };

  const validateSubmission = () => {
    if (!isEngaged) {
      toast.error(`Please spend at least ${Math.ceil(minimumEngagementTime / 60)} minutes on the legal analysis.`);
      return false;
    }

    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(field => {
      const value = inputs[field];
      return !value || (Array.isArray(value) ? value.every(v => !v) : !value.length);
    });
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields before submitting.');
      return false;
    }

    return true;
  };

  const getRequiredFields = () => {
    switch (exerciseType) {
      case 'case-law-analysis':
        return ['facts', 'analysis', 'conclusion'];
      case 'legal-document-drafting':
        return ['documentType', 'legalLanguage'];
      case 'constitutional-interpretation':
        return ['constitutionalProvision', 'interpretation'];
      case 'evidence-evaluation':
        return ['admissibility', 'relevance'];
      case 'ethical-dilemma':
        return ['resolution', 'justification'];
      default:
        return [];
    }
  };

  const evaluateSubmission = () => {
    switch (exerciseType) {
      case 'case-law-analysis':
        return evaluateCaseLaw();
      case 'legal-document-drafting':
        return evaluateDocumentDrafting();
      case 'constitutional-interpretation':
        return evaluateConstitutionalInterpretation();
      case 'evidence-evaluation':
        return evaluateEvidence();
      case 'ethical-dilemma':
        return evaluateEthics();
      default:
        return { score: 0, feedback: 'Unknown exercise type' };
    }
  };

  const evaluateCaseLaw = () => {
    let score = 0;
    const feedback = [];

    // Facts identification (20 points)
    if (inputs.facts?.length > 80) {
      score += 20;
      feedback.push('✓ Facts clearly identified');
    } else {
      feedback.push('Provide more detailed factual analysis');
    }

    // Legal issues (25 points)
    const validIssues = inputs.legalIssues?.filter((issue: string) => issue.length > 20).length || 0;
    score += Math.min(25, validIssues * 12.5);
    if (validIssues >= 2) {
      feedback.push('✓ Legal issues well identified');
    }

    // Legal analysis (35 points)
    if (inputs.analysis?.length > 150) {
      score += 35;
      feedback.push('✓ Comprehensive legal analysis');
    } else {
      feedback.push('Expand your legal reasoning and analysis');
    }

    // Conclusion (20 points)
    if (inputs.conclusion?.length > 50) {
      score += 20;
      feedback.push('✓ Sound legal conclusion');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateDocumentDrafting = () => {
    let score = 0;
    const feedback = [];

    // Document structure (25 points)
    if (inputs.documentType && inputs.parties?.filter((p: string) => p.length > 5).length >= 2) {
      score += 25;
      feedback.push('✓ Document properly structured');
    }

    // Legal language (35 points)
    if (inputs.legalLanguage?.length > 100) {
      score += 35;
      feedback.push('✓ Appropriate legal language used');
    } else {
      feedback.push('Use more precise legal terminology');
    }

    // Terms and clauses (25 points)
    const validTerms = inputs.terms?.filter((term: string) => term.length > 15).length || 0;
    score += Math.min(25, validTerms * 8.33);

    // Professional formatting (15 points)
    if (inputs.clauses?.filter((c: string) => c.length > 20).length >= 1) {
      score += 15;
      feedback.push('✓ Professional clauses included');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateConstitutionalInterpretation = () => {
    let score = 0;
    const feedback = [];

    // Constitutional provision analysis (30 points)
    if (inputs.constitutionalProvision?.length > 50) {
      score += 30;
      feedback.push('✓ Constitutional provision analyzed');
    }

    // Interpretation (40 points)
    if (inputs.interpretation?.length > 100) {
      score += 40;
      feedback.push('✓ Thoughtful constitutional interpretation');
    }

    // Legal reasoning (20 points)
    if (inputs.reasoning?.length > 60) {
      score += 20;
      feedback.push('✓ Legal reasoning provided');
    }

    // Implications (10 points)
    if (inputs.implications?.length > 30) {
      score += 10;
      feedback.push('✓ Broader implications considered');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateEvidence = () => {
    let score = 0;
    const feedback = [];

    // Evidence admissibility (35 points)
    if (inputs.admissibility?.length > 60) {
      score += 35;
      feedback.push('✓ Evidence admissibility analyzed');
    }

    // Relevance analysis (25 points)
    if (inputs.relevance?.length > 40) {
      score += 25;
      feedback.push('✓ Evidence relevance assessed');
    }

    // Reliability assessment (25 points)
    if (inputs.reliability?.length > 40) {
      score += 25;
      feedback.push('✓ Evidence reliability evaluated');
    }

    // Legal objections (15 points)
    if (inputs.objections?.filter((obj: string) => obj.length > 10).length >= 1) {
      score += 15;
      feedback.push('✓ Potential objections identified');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateEthics = () => {
    let score = 0;
    const feedback = [];

    // Ethical issues identification (30 points)
    const validIssues = inputs.ethicalIssues?.filter((issue: string) => issue.length > 20).length || 0;
    score += Math.min(30, validIssues * 30);

    // Conflicting duties analysis (25 points)
    const validDuties = inputs.conflictingDuties?.filter((duty: string) => duty.length > 15).length || 0;
    score += Math.min(25, validDuties * 12.5);

    // Resolution (30 points)
    if (inputs.resolution?.length > 80) {
      score += 30;
      feedback.push('✓ Ethical resolution provided');
    }

    // Justification (15 points)
    if (inputs.justification?.length > 50) {
      score += 15;
      feedback.push('✓ Ethical reasoning justified');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const handleSubmit = () => {
    if (!validateSubmission()) return;

    const evaluation = evaluateSubmission();
    const finalScore = Math.max(0, Math.min(100, evaluation.score));
    const passed = finalScore >= 70;

    setResults({
      score: finalScore,
      feedback: evaluation.feedback,
      passed,
      timeSpent,
      interactions: inputInteractions
    });

    setIsSubmitted(true);
    setIsCorrect(passed);
    
    onComplete(passed, finalScore);

    if (passed) {
      toast.success(`Excellent legal analysis! Score: ${finalScore}%`);
    } else {
      toast.error(`Score: ${finalScore}%. Review the feedback and try again.`);
    }
  };

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'case-law-analysis': return <ClipboardDocumentCheckIcon className="h-6 w-6" />;
      case 'legal-document-drafting': return <DocumentTextIcon className="h-6 w-6" />;
      case 'constitutional-interpretation': return <ScaleIcon className="h-6 w-6" />;
      case 'evidence-evaluation': return <ExclamationTriangleIcon className="h-6 w-6" />;
      case 'ethical-dilemma': return <BriefcaseIcon className="h-6 w-6" />;
      default: return <ScaleIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseContent = () => {
    switch (exerciseType) {
      case 'case-law-analysis':
        return (
          <div className="space-y-6">
            {scenario?.caseDetails && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Case Details:</h4>
                <div className="text-sm text-gray-700 whitespace-pre-line">{scenario.caseDetails}</div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Facts of the Case *
              </label>
              <textarea
                value={inputs.facts || ''}
                onChange={(e) => handleInputChange('facts', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Identify and summarize the key facts of this case"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Legal Issues
              </label>
              {[0, 1].map((index) => (
                <textarea
                  key={index}
                  value={inputs.legalIssues?.[index] || ''}
                  onChange={(e) => {
                    const newIssues = [...(inputs.legalIssues || ['', ''])];
                    newIssues[index] = e.target.value;
                    handleInputChange('legalIssues', newIssues);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                  rows={2}
                  placeholder={`Legal Issue ${index + 1}: What legal questions does this case raise?`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Relevant Laws and Precedents
              </label>
              {[0, 1].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={inputs.relevantLaws?.[index] || ''}
                  onChange={(e) => {
                    const newLaws = [...(inputs.relevantLaws || ['', ''])];
                    newLaws[index] = e.target.value;
                    handleInputChange('relevantLaws', newLaws);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                  placeholder={`Law/Precedent ${index + 1}: Cite relevant statutes or case law`}
                  disabled={isSubmitted}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Legal Analysis *
              </label>
              <textarea
                value={inputs.analysis || ''}
                onChange={(e) => handleInputChange('analysis', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={5}
                placeholder="Provide detailed legal analysis applying the law to the facts"
                disabled={isSubmitted}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conclusion *
              </label>
              <textarea
                value={inputs.conclusion || ''}
                onChange={(e) => handleInputChange('conclusion', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="State your legal conclusion and its basis"
                disabled={isSubmitted}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="mb-4">
              {getExerciseIcon()}
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              {exerciseType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} Exercise
            </h4>
            <p className="text-gray-600 mb-6">
              This legal exercise is being developed with specialized legal analysis tools.
            </p>
            
            <button
              onClick={() => onComplete(true, 85)}
              className="bg-blue-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-800 transition-colors"
            >
              Complete Exercise (Temporary)
            </button>
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-800 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getExerciseIcon()}
            <div className="ml-3">
              <h3 className="text-xl font-bold">{content.title}</h3>
              <p className="text-blue-100 text-sm">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-300' : 'bg-red-300'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            <div className="text-sm">
              Time: {formatTime(timeSpent)}
            </div>
            
            {isEngaged && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-green-500 bg-opacity-20 text-green-100">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Engaged
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">{instructions}</p>
        
        {!isEngaged && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Minimum legal analysis time</span>
              <span>{formatTime(timeSpent)} / {formatTime(minimumEngagementTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-900 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumEngagementTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Exercise Content */}
      <div className="p-6">
        {renderExerciseContent()}

        {hints && hints.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center text-blue-900 hover:text-blue-800 mb-3"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </button>
            
            {showHints && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Hint {currentHint + 1} of {hints.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentHint(Math.max(0, currentHint - 1))}
                      disabled={currentHint === 0}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentHint(Math.min(hints.length - 1, currentHint + 1))}
                      disabled={currentHint === hints.length - 1}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <p className="text-yellow-700 text-sm">{hints[currentHint]}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              className="bg-blue-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-800 transition-colors"
            >
              Submit Legal Analysis
            </button>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                {isCorrect ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                )}
                <span className="font-medium">
                  Score: {results.score}% {isCorrect ? '(Passed)' : '(Review Required)'}
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {results.feedback}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Time spent: {formatTime(results.timeSpent)} | Interactions: {results.interactions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LawInteractive;